import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ServerIcon, LockClosedIcon, PhoneIcon, KeyIcon } from '@heroicons/react/24/outline';
import { authAPI } from '../services/api';

const Login = () => {
  const [loginMethod, setLoginMethod] = useState('phone'); // 'phone' or 'password'
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();

  // Countdown timer para reenvio de OTP
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const formatPhoneNumber = (value) => {
    // Remove tudo que não é número
    const numbers = value.replace(/\D/g, '');
    
    // Formata para o padrão brasileiro
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Limpa o número e adiciona código do país
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      const fullPhone = cleanPhone.startsWith('55') ? `+${cleanPhone}` : `+55${cleanPhone}`;

      const response = await authAPI.requestOTP({ phone: fullPhone });
      
      if (response.data.debug) {
        // Em desenvolvimento, mostra o código
        setSuccess(`Código enviado! (Dev: ${response.data.debug.otp})`);
      } else {
        setSuccess('Código enviado via WhatsApp!');
      }
      
      setStep('otp');
      setCountdown(60); // Inicia countdown de 60 segundos
    } catch (error) {
      console.error('Erro ao solicitar OTP:', error);
      setError(error.response?.data?.error || 'Erro ao enviar código');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const cleanPhone = phoneNumber.replace(/\D/g, '');
      const fullPhone = cleanPhone.startsWith('55') ? `+${cleanPhone}` : `+55${cleanPhone}`;

      const response = await authAPI.login({ phone: fullPhone, otp: otpCode });
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      navigate('/dashboard');
    } catch (error) {
      console.error('Erro no login:', error);
      setError(error.response?.data?.error || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await authAPI.login(formData);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      navigate('/dashboard');
    } catch (error) {
      console.error('Erro no login:', error);
      setError(error.response?.data?.error || 'Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6">
        <div>
          <div className="mx-auto h-16 w-16 flex items-center justify-center rounded-full bg-blue-100">
            <ServerIcon className="h-10 w-10 text-blue-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Server Manager
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Sistema de Gerenciamento de Servidor
          </p>
        </div>

        {/* Seletor de método de login */}
        <div className="flex rounded-lg bg-gray-100 p-1">
          <button
            type="button"
            onClick={() => {
              setLoginMethod('phone');
              setStep('phone');
              setError('');
              setSuccess('');
            }}
            className={`flex-1 flex items-center justify-center py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              loginMethod === 'phone'
                ? 'bg-white text-blue-700 shadow'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <PhoneIcon className="h-5 w-5 mr-2" />
            WhatsApp
          </button>
          <button
            type="button"
            onClick={() => {
              setLoginMethod('password');
              setError('');
              setSuccess('');
            }}
            className={`flex-1 flex items-center justify-center py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              loginMethod === 'password'
                ? 'bg-white text-blue-700 shadow'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <KeyIcon className="h-5 w-5 mr-2" />
            Senha
          </button>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {success && (
          <div className="rounded-md bg-green-50 p-4">
            <p className="text-sm text-green-800">{success}</p>
          </div>
        )}

        {/* Login via WhatsApp */}
        {loginMethod === 'phone' && (
          <>
            {step === 'phone' && (
              <form className="mt-8 space-y-6" onSubmit={handleRequestOTP}>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                    Número de WhatsApp
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">+55</span>
                    </div>
                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      required
                      className="pl-12 input w-full"
                      placeholder="(11) 99999-9999"
                      value={phoneNumber}
                      onChange={handlePhoneChange}
                      maxLength="15"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Digite apenas o número com DDD
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading || phoneNumber.replace(/\D/g, '').length < 10}
                  className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                >
                  {loading ? 'Enviando...' : 'Enviar código via WhatsApp'}
                </button>
              </form>
            )}

            {step === 'otp' && (
              <form className="mt-8 space-y-6" onSubmit={handleOTPLogin}>
                <div>
                  <label htmlFor="otp" className="block text-sm font-medium text-gray-700">
                    Código de verificação
                  </label>
                  <input
                    id="otp"
                    name="otp"
                    type="text"
                    required
                    className="mt-1 input w-full text-center text-2xl tracking-widest"
                    placeholder="000000"
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength="6"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Digite o código de 6 dígitos enviado via WhatsApp
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={loading || otpCode.length !== 6}
                    className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
                  >
                    {loading ? 'Verificando...' : 'Entrar'}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setStep('phone');
                      setOtpCode('');
                      setError('');
                      setSuccess('');
                    }}
                    disabled={countdown > 0}
                    className="w-full text-sm text-blue-600 hover:text-blue-500 disabled:text-gray-400"
                  >
                    {countdown > 0 ? `Reenviar código em ${countdown}s` : 'Solicitar novo código'}
                  </button>
                </div>
              </form>
            )}
          </>
        )}

        {/* Login tradicional com senha */}
        {loginMethod === 'password' && (
          <form className="mt-8 space-y-6" onSubmit={handlePasswordLogin}>
            <div className="space-y-4">
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                  Usuário
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  className="mt-1 input w-full"
                  placeholder="Digite seu usuário"
                  value={formData.username}
                  onChange={handleChange}
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Senha
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="mt-1 input w-full"
                  placeholder="Digite sua senha"
                  value={formData.password}
                  onChange={handleChange}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors"
            >
              <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                <LockClosedIcon className="h-5 w-5 text-blue-500 group-hover:text-blue-400" />
              </span>
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default Login;